import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, ChevronDown, Search, ScrollText } from 'lucide-react'
import { Shell, useShellMeta } from './Shell.js'
import { Modal, Select, apiGet } from './primitives.js'
import { auditMeta, subjectLabel } from './catalog.js'

/**
 * /audit (LOT 5.5.2) — le journal en LANGAGE HUMAIN.
 *
 * Le tableau ne montre plus un seul code technique : chaque événement
 * affiche son libellé du catalogue (« Connexion réussie », « Rôle
 * modifié »…) et son message humain. Cliquer un événement ouvre le détail :
 * RÉSUMÉ humain d'abord, DÉTAILS TECHNIQUES (code, clés, trace, avant/après)
 * repliés par défaut — rien n'est supprimé de la donnée, tout reste
 * accessible quand on décide d'aller le chercher.
 */

interface AuditEvent {
  id: number; occurred_at: string
  actor_type: string; actor_id: string | null; actor_name: string | null
  event_type: string; severity: string
  subject_type: string | null; subject_id: string | null
  decision: string | null; human_message: string | null; trace_id: string | null
  old_value: unknown; new_value: unknown
}

const PAGE = 25

function qsFromUrl(): { action: string; actor: string; q: string; trace: string } {
  const p = new URLSearchParams(window.location.search)
  return { action: p.get('action') ?? '', actor: p.get('actor') ?? '', q: p.get('q') ?? '', trace: p.get('trace_id') ?? '' }
}

export function AuditPage() {
  const meta = useShellMeta()
  const [filters, setFilters] = useState(qsFromUrl)
  const [offset, setOffset] = useState(0)
  const [data, setData] = useState<{ total: number; items: AuditEvent[]; event_types: string[] } | null>(null)
  const [selected, setSelected] = useState<AuditEvent | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const debounce = useRef<ReturnType<typeof setTimeout>>(undefined)

  const charger = useCallback(() => {
    const p = new URLSearchParams()
    if (filters.action) p.set('action', filters.action)
    if (filters.actor) p.set('actor', filters.actor)
    if (filters.q) p.set('q', filters.q)
    if (filters.trace) p.set('trace_id', filters.trace)
    window.history.replaceState(null, '', `/audit${p.size ? `?${p}` : ''}`)
    p.set('limit', String(PAGE))
    p.set('offset', String(offset))
    apiGet<{ total: number; items: AuditEvent[]; event_types: string[] }>(`/api/v1/audit?${p}`)
      .then(setData)
      .catch(() => undefined)
  }, [filters, offset])

  useEffect(() => { charger() }, [charger])

  const filtrerTrace = (trace: string) => {
    setFilters({ action: '', actor: '', q: '', trace })
    setOffset(0)
    setDetailOpen(false)
  }

  const fmtTs = (iso: string) =>
    new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })

  const acteur = (e: AuditEvent) =>
    e.actor_name ?? (e.actor_type === 'SYSTEM' ? 'Système' : e.actor_id ? 'Compte supprimé' : '—')

  return (
    <Shell active="audit" meta={meta}>
      <main className="lc-page lc-stagger">
        <h1 className="lc-title">Audit</h1>
        <p className="lc-subtitle">
          Qui a fait quoi, quand — et pourquoi Gestion a pris une décision.
          L’historique est immuable : personne ne le réécrit, pas même un Super Admin.
        </p>

        <div className="ad-toolbar">
          <div className="lc-search">
            <Search size={18} className="lc-search__icon" />
            <input
              placeholder="Rechercher dans les messages…"
              aria-label="Recherche audit"
              defaultValue={filters.q}
              onChange={(e) => {
                clearTimeout(debounce.current)
                const v = e.target.value
                debounce.current = setTimeout(() => { setFilters((f) => ({ ...f, q: v })); setOffset(0) }, 350)
              }}
            />
          </div>
          <Select label="Action" compact value={filters.action}
            options={[
              { value: '', label: 'Toutes' },
              ...(data?.event_types ?? [])
                .map((t) => ({ value: t, label: auditMeta(t).label }))
                .sort((a, b) => a.label.localeCompare(b.label, 'fr')),
            ]}
            onChange={(v) => { setFilters((f) => ({ ...f, action: v })); setOffset(0) }} />
          <input
            className="ad-input" placeholder="Acteur (nom ou e-mail)"
            aria-label="Filtrer par acteur" defaultValue={filters.actor}
            onChange={(e) => {
              clearTimeout(debounce.current)
              const v = e.target.value
              debounce.current = setTimeout(() => { setFilters((f) => ({ ...f, actor: v })); setOffset(0) }, 350)
            }}
          />
          {filters.trace ? (
            <button className="lc-btn lc-btn--ghost" onClick={() => filtrerTrace('')}>
              Même opération ✕
            </button>
          ) : null}
          <span className="ad-count">{data ? `${data.total} événements` : '…'}</span>
        </div>

        <section className="lc-panel gd-panel ad-audit-table">
          <div className="lc-panel__body lc-panel__body--flush" style={{ overflowX: 'auto' }}>
            <table className="lc-table">
              <thead>
                <tr>
                  <th>Quand</th><th>Qui</th><th>Quoi</th><th>Résultat</th><th></th>
                </tr>
              </thead>
              <tbody>
                {(data?.items ?? []).map((e) => (
                  <tr key={e.id} onClick={() => { setSelected(e); setDetailOpen(true) }} style={{ cursor: 'pointer' }}>
                    <td className="ad-mono" style={{ whiteSpace: 'nowrap' }}>{fmtTs(e.occurred_at)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{acteur(e)}</td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{auditMeta(e.event_type).label}</div>
                      {e.human_message ? (
                        <div style={{ color: 'var(--text-3)', fontSize: 'var(--fs-13)', maxWidth: 520, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {e.human_message}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <span className={`lc-pill ${e.severity === 'WARNING' ? 'lc-pill--warning' : e.severity === 'ERROR' || e.severity === 'CRITICAL' ? 'lc-pill--error' : 'lc-pill--idle'}`}>
                        {e.severity === 'INFO' ? 'OK' : e.severity === 'WARNING' ? 'Attention' : 'Erreur'}
                      </span>
                    </td>
                    <td style={{ color: 'var(--text-3)', fontSize: 'var(--fs-13)', whiteSpace: 'nowrap' }}>Voir les détails</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <div className="ad-toolbar">
          <button className="lc-btn lc-btn--ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
            <ChevronLeft size={15} /> Précédent
          </button>
          <button className="lc-btn lc-btn--ghost" disabled={!data || offset + PAGE >= data.total} onClick={() => setOffset(offset + PAGE)}>
            Suivant <ChevronRight size={15} />
          </button>
          <span className="ad-count">
            {data ? `${offset + 1}–${Math.min(offset + PAGE, data.total)} sur ${data.total} · journaux techniques détaillés disponibles côté serveur` : ''}
          </span>
        </div>

        {selected ? (
          <AuditDetailModal
            event={selected}
            open={detailOpen}
            onClose={() => setDetailOpen(false)}
            onExited={() => setSelected(null)}
            onTrace={filtrerTrace}
          />
        ) : null}
      </main>
    </Shell>
  )
}

/* ═══════════ DÉTAIL — résumé humain, technique replié ═══════════ */

function AuditDetailModal(props: {
  event: AuditEvent
  open: boolean
  onClose: () => void
  onExited: () => void
  onTrace: (trace: string) => void
}) {
  const e = props.event
  const meta = auditMeta(e.event_type)
  const [tech, setTech] = useState(false)
  useEffect(() => { setTech(false) }, [e.id])

  const fmtTs = (iso: string) =>
    new Date(iso).toLocaleString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', second: '2-digit' })

  return (
    <Modal open={props.open} onClose={props.onClose} onExited={props.onExited} ariaLabel={meta.label} width={640}>
      <header className="lc-modal__head">
        <span className="lc-modal__icon"><ScrollText size={20} /></span>
        <div>
          <div className="lc-modal__title">{meta.label}</div>
          <div className="lc-modal__pills">
            <span className="lc-pill lc-pill--idle">{meta.category}</span>
            <span className={`lc-pill ${e.severity === 'WARNING' ? 'lc-pill--warning' : e.severity === 'ERROR' || e.severity === 'CRITICAL' ? 'lc-pill--error' : 'lc-pill--ok'}`}>
              {e.severity === 'INFO' ? 'Déroulement normal' : e.severity === 'WARNING' ? 'À surveiller' : 'Erreur'}
            </span>
          </div>
        </div>
      </header>
      <div className="lc-modal__body">
        {/* ─── Résumé, tout en humain ─── */}
        <dl className="lc-kv">
          <dt>Action</dt><dd>{meta.label}</dd>
          <dt>Acteur</dt><dd>{e.actor_name ?? (e.actor_type === 'SYSTEM' ? 'Système' : 'Compte supprimé')}</dd>
          <dt>Date</dt><dd>{fmtTs(e.occurred_at)}</dd>
          <dt>Cible</dt><dd>{e.human_message ? subjectLabel(e.subject_type, null) || '—' : subjectLabel(e.subject_type, e.subject_id)}</dd>
          {e.decision ? (<><dt>Résultat</dt><dd>{e.decision}</dd></>) : null}
          <dt>Détail</dt><dd>{e.human_message ?? 'Aucun détail supplémentaire.'}</dd>
        </dl>

        {e.trace_id ? (
          <button className="lc-btn" style={{ marginTop: 'var(--sp-4)' }}
            onClick={() => props.onTrace(e.trace_id!)}>
            Voir toute l’opération liée
          </button>
        ) : null}

        {/* ─── Détails techniques : repliés par défaut ─── */}
        <button
          className="lc-btn lc-btn--ghost ad-tech-toggle"
          onClick={() => setTech(!tech)}
          aria-expanded={tech}
          style={{ marginTop: 'var(--sp-5)', fontSize: 'var(--fs-12)' }}
        >
          <ChevronDown size={14} style={{ transform: tech ? 'rotate(180deg)' : undefined, transition: 'transform .15s ease' }} />
          Détails techniques
        </button>
        {tech ? (
          <dl className="lc-kv ad-tech" style={{ marginTop: 'var(--sp-3)' }}>
            <dt>Code action</dt><dd className="ad-mono">{e.event_type}</dd>
            <dt>Identifiant</dt><dd className="ad-mono">#{e.id}</dd>
            <dt>Type de cible</dt><dd className="ad-mono">{e.subject_type ?? '—'}</dd>
            <dt>Identifiant cible</dt><dd className="ad-mono" style={{ overflowWrap: 'anywhere' }}>{e.subject_id ?? '—'}</dd>
            <dt>Acteur (id)</dt><dd className="ad-mono" style={{ overflowWrap: 'anywhere' }}>{e.actor_id ?? '—'}</dd>
            <dt>Trace</dt><dd className="ad-mono" style={{ overflowWrap: 'anywhere' }}>{e.trace_id ?? '—'}</dd>
            {e.old_value != null ? (
              <><dt>Avant</dt><dd><pre className="ad-mono" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify(e.old_value, null, 2)}</pre></dd></>
            ) : null}
            {e.new_value != null ? (
              <><dt>Après</dt><dd><pre className="ad-mono" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify(e.new_value, null, 2)}</pre></dd></>
            ) : null}
          </dl>
        ) : null}
      </div>
    </Modal>
  )
}
